import React from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Play,
  Settings,
} from 'lucide-react';
import { ValidationPanel, type ValidationIssue } from './ValidationPanel';
import { ExecutionPanel } from './ExecutionPanel';
import { CapabilityRequirementsEditor } from './CapabilityRequirementsEditor';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Modem } from '@/types/firestore';
import type { PrimitiveCapabilityRequirements } from '@simcheck/shared-types';

export type BottomPanelTab = 'validation' | 'execution' | 'settings';

interface BottomPanelProps {
  // Validation
  validationIssues: ValidationIssue[];
  onIssueClick: (issue: ValidationIssue) => void;

  // Execution
  flowId: string;
  nodes: any[];
  edges: any[];
  modems: Modem[];
  onHighlightNode?: (nodeId: string | null) => void;
  isPrimitive?: boolean;
  hasValidationErrors?: boolean;

  // Capability requirements (primitives only)
  capabilityRequirements?: PrimitiveCapabilityRequirements;
  onCapabilityRequirementsChange?: (requirements: PrimitiveCapabilityRequirements) => void;

  // Collapse / expand wiring
  collapsed: boolean;
  onExpandRequest: () => void;
  onCollapseRequest: () => void;
  statusLine: string;

  // Controlled tab state
  activeTab: BottomPanelTab;
  onActiveTabChange: (tab: BottomPanelTab) => void;
}

export function BottomPanel({
  validationIssues,
  onIssueClick,
  flowId,
  nodes,
  edges,
  modems,
  onHighlightNode,
  isPrimitive = false,
  hasValidationErrors = false,
  capabilityRequirements,
  onCapabilityRequirementsChange,
  collapsed,
  onExpandRequest,
  onCollapseRequest,
  statusLine,
  activeTab,
  onActiveTabChange,
}: BottomPanelProps) {
  const errorCount = validationIssues.filter((i) => i.severity === 'error').length;
  const warningCount = validationIssues.filter((i) => i.severity === 'warning').length;
  const hasErrors = errorCount > 0;

  // Auto-switch to validation when errors first appear and we're on the
  // execution tab. Preserves the previous behavior so a failing run can't
  // hide its error behind the active tab.
  const prevHasErrors = React.useRef(hasErrors);
  React.useEffect(() => {
    if (hasErrors && !prevHasErrors.current && activeTab === 'execution') {
      onActiveTabChange('validation');
    }
    prevHasErrors.current = hasErrors;
  }, [hasErrors, activeTab, onActiveTabChange]);

  // Collapsed: single-row status strip.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onExpandRequest}
        className={cn(
          'h-full w-full flex items-center justify-between gap-3 px-3',
          'bg-card border-t border-border dark:border-border-subtle',
          'text-[12px] text-muted-foreground hover:bg-muted/40 transition-colors text-left'
        )}
        title="Expand log"
      >
        <div className="flex items-center gap-2 min-w-0">
          {hasErrors ? (
            <AlertTriangle className="w-3.5 h-3.5 text-destructive flex-shrink-0" />
          ) : (
            <CheckCircle className="w-3.5 h-3.5 text-status-online flex-shrink-0" />
          )}
          <span className="truncate">{statusLine}</span>
        </div>
        <ChevronUp className="w-3.5 h-3.5 flex-shrink-0" />
      </button>
    );
  }

  // Expanded: tab bar + content.
  return (
    <div className="h-full flex flex-col bg-card border-t border-border dark:border-border-subtle">
      <div className="flex items-center justify-between border-b border-border dark:border-border-subtle px-2 flex-shrink-0">
        <div className="flex items-center gap-0">
          <TabButton
            active={activeTab === 'validation'}
            onClick={() => onActiveTabChange('validation')}
            icon={hasErrors ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
            label="Validation"
            badge={
              validationIssues.length > 0 ? (
                <Badge
                  variant={hasErrors ? 'destructive' : 'outline'}
                  className={cn('ml-1 h-4 px-1 text-[10px]', !hasErrors && 'border-accent-yellow text-accent-yellow')}
                >
                  {errorCount > 0 && `${errorCount}E `}
                  {warningCount > 0 && `${warningCount}W`}
                </Badge>
              ) : null
            }
          />
          <TabButton
            active={activeTab === 'execution'}
            onClick={() => onActiveTabChange('execution')}
            icon={<Play className="w-3.5 h-3.5" />}
            label="Execution"
          />
          {isPrimitive && (
            <TabButton
              active={activeTab === 'settings'}
              onClick={() => onActiveTabChange('settings')}
              icon={<Settings className="w-3.5 h-3.5" />}
              label="Requirements"
            />
          )}
        </div>
        <button
          type="button"
          onClick={onCollapseRequest}
          className="text-muted-foreground/70 hover:text-foreground transition-colors p-1"
          title="Collapse log"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        {activeTab === 'validation' && (
          <div className="flex-1 overflow-y-auto p-3">
            <ValidationPanel issues={validationIssues} onIssueClick={onIssueClick} onClose={() => {}} />
          </div>
        )}
        {activeTab === 'execution' && (
          <div className="flex-1 overflow-hidden">
            <ExecutionPanel
              isOpen={true}
              onClose={() => {}}
              flowId={flowId}
              nodes={nodes}
              edges={edges}
              modems={modems}
              onHighlightNode={onHighlightNode}
              isPrimitive={isPrimitive}
              hasValidationErrors={hasValidationErrors}
            />
          </div>
        )}
        {activeTab === 'settings' && isPrimitive && capabilityRequirements && onCapabilityRequirementsChange && (
          <div className="flex-1 overflow-y-auto p-3">
            <CapabilityRequirementsEditor
              requirements={capabilityRequirements}
              onChange={onCapabilityRequirementsChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-accent-blue text-accent-blue'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      )}
    >
      {icon}
      <span>{label}</span>
      {badge}
    </button>
  );
}
